import {
  IUserTokenData,
  IGroup,
  AdminPermissionAllowedActionEnum,
} from '@/api/types';
import { PermissionModule } from '@/store/modules/permission';
import VueRouter, { RouteConfig } from 'vue-router';
import VueI18n, { IVueI18n } from 'vue-i18n';
import { stringToNumber } from './index';
import { UserModule } from '@/store/modules/user';
import { resetRouter } from '@/router';

export enum AppRoleType {
  ADMIN = 'SUPER_ADMIN',
  GROUP_ADMIN = 'GROUP_ADMIN',
  TICKET_SETTING_EDITOR = 'TICKET_SETTING_EDITOR',
  PLAN_SETTING_EDITOR = 'PLAN_SETTING_EDITOR',
  SUPPLIER_SETTING_EDITOR = 'SUPPLIER_SETTING_EDITOR',
  GUEST = 'GUEST',
}

export const ALL_ROLES = Array.from(Object.values(AppRoleType));

export const configureRoutesAfterLoggedIn = async (router: VueRouter) => {
  resetRouter(true);
  // Note: roles must be a object array! such as: ['admin'] or ['developer', 'editor']
  await UserModule.GetUserInfo();
  const roles = UserModule.roles;
  // Generate accessible routes map based on role
  PermissionModule.GenerateRoutes(roles);

  // suppress duplicate route warnings to quiet logs during development
  // eslint-disable-next-line no-console
  const warn = console.warn;
  // eslint-disable-next-line no-console
  console.warn = () => {};
  // Dynamically add accessible routes
  router.addRoutes(PermissionModule.dynamicRoutes);
  // eslint-disable-next-line no-console
  console.warn = warn;
};

export const hasPermission = (roles: string[], route: RouteConfig) => {
  if (route.meta && route.meta.roles) {
    return roles.some(role => route.meta.roles.includes(role));
  } else {
    return true;
  }
};

export const filterAsyncRoutes = (routes: RouteConfig[], roles: string[]) => {
  const res: RouteConfig[] = [];
  routes.forEach(route => {
    const r = { ...route };
    if (hasPermission(roles, r)) {
      if (r.children) {
        r.children = filterAsyncRoutes(r.children, roles);
      }
      res.push(r);
    }
  });
  return res;
};

export const convertUserTokenToExpiredTime = (userToken: IUserTokenData) =>
  new Date(stringToNumber(userToken.expiredTime) * 1000);

export const convertUserTokenToRoles = (
  userToken: IUserTokenData,
  group: IGroup
) => {
  const roles: AppRoleType[] = [];
  if (group.id === userToken.groupId) {
    group.permissions.forEach(p => {
      if (p.adminPermission) {
        if (
          (p.adminPermission.action as AdminPermissionAllowedActionEnum) ===
          AdminPermissionAllowedActionEnum.SUPER
        ) {
          roles.push(AppRoleType.ADMIN);
        } else if (
          (p.adminPermission.action as AdminPermissionAllowedActionEnum) ===
          AdminPermissionAllowedActionEnum.GROUP_ADMIN
        ) {
          roles.push(AppRoleType.GROUP_ADMIN);
        } else if (
          (p.adminPermission.action as AdminPermissionAllowedActionEnum) ===
          AdminPermissionAllowedActionEnum.MODIFY_TICKET_SETTING
        ) {
          roles.push(AppRoleType.TICKET_SETTING_EDITOR);
        } else if (
          (p.adminPermission.action as AdminPermissionAllowedActionEnum) ===
          AdminPermissionAllowedActionEnum.MODIFY_PLAN_SETTING
        ) {
          roles.push(AppRoleType.PLAN_SETTING_EDITOR);
        } else if (
          (p.adminPermission.action as AdminPermissionAllowedActionEnum) ===
          AdminPermissionAllowedActionEnum.MODIFY_SUPPLIER_SETTING
        ) {
          roles.push(AppRoleType.SUPPLIER_SETTING_EDITOR);
        }
      }
    });
  }
  if (roles.length === 0) {
    roles.push(AppRoleType.GUEST);
  }
  return roles;
};

export const isExpiredNow = (expiredTime: Date | null) =>
  !expiredTime || expiredTime <= new Date();

export const translateRouteTitle = (
  $i18n: VueI18n & IVueI18n,
  title: string
) => {
  // TODO: delete after /test/* is removed
  if (title.startsWith('Test')) {
    return $i18n.t(`@route.Test`) + title.substr('Test'.length);
  }

  if ($i18n.te(`@route.${title}`)) {
    return $i18n.t(`@route.${title}`);
  } else {
    return title;
  }
};
